import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import { Breadcrumb, FinalCTA, SisterServices } from "../components/site/ServiceSections";
import { BLOG_POSTS, BLOG_CATEGORIES } from "../data/blog";

export default function BlogHub() {
  const [filter, setFilter] = useState("all");
  const posts = filter === "all" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === filter);
  const [feature, ...rest] = posts;

  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 lg:pt-40 pb-12 lg:pb-16 bg-ink">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <Breadcrumb current="Journal" />
          <div className="max-w-4xl">
            <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 font-bold">Journal · 14 articles · Updated 2026</p>
            <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)" }}>
              The Restaurant
              <br />
              Photographer <span className="italic font-light text-ember">Journal.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-bone/80">
              Honest writing on hospitality photography — what it costs, how to brief it, which Melbourne suburbs photograph best, and how to get the most out of every shoot day.
            </p>
          </div>

          {/* Filter chips */}
          <div className="mt-10 flex flex-wrap gap-2">
            {[{ id: "all", label: "All" }, ...BLOG_CATEGORIES].map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`blog-filter-${c.id}`}
                onClick={() => setFilter(c.id)}
                className={`px-5 py-2 text-[11px] uppercase tracking-wider2 font-bold border transition-all ${
                  filter === c.id ? "border-ember bg-ember text-bone" : "border-bone/20 text-bone/70 hover:border-ember hover:text-ember"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      {feature && (
        <section className="bg-ink text-bone pb-16">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <Link to={`/blog/${feature.slug}`} data-testid={`blog-feature-${feature.slug}`} className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
              <div className="lg:col-span-7 relative overflow-hidden">
                <Photo slug={feature.hero} alt={feature.title} ratio="16/10" sizes="(min-width: 1024px) 60vw, 100vw" />
              </div>
              <div className="lg:col-span-5">
                <p className="text-xs uppercase tracking-wider2 text-ember mb-4 font-bold">
                  Featured · {BLOG_CATEGORIES.find((c) => c.id === feature.category)?.label}
                </p>
                <h2 className="font-display tracking-editorial leading-[0.95] group-hover:text-ember transition-colors" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
                  {feature.title}
                </h2>
                <p className="mt-5 text-base md:text-lg text-bone/75 leading-relaxed">{feature.dek}</p>
                <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-wider2 font-bold text-ember">
                  <Clock size={12} strokeWidth={2} /> {feature.readMin} min read <ArrowUpRight size={14} strokeWidth={2} className="ml-2 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="bg-bone text-ink py-20 lg:py-28">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <p className="text-xs uppercase tracking-wider2 text-ember mb-3 font-bold">More from the journal</p>
          <h2 className="font-display tracking-editorial leading-[0.95] mb-12" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
            {rest.length} more
            <span className="italic font-light text-ember"> articles.</span>
          </h2>

          {rest.length === 0 ? (
            <p className="text-char-mute italic">No articles in this category yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
              {rest.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group bg-bone hover:bg-ink hover:text-bone transition-colors p-6 md:p-7 flex flex-col"
                >
                  <Photo slug={p.hero} alt={p.title} ratio="4/3" sizes="(min-width: 1024px) 30vw, 50vw" />
                  <p className="mt-5 text-[10px] uppercase tracking-wider2 font-bold text-ember">
                    {BLOG_CATEGORIES.find((c) => c.id === p.category)?.label} · {p.readMin} min
                  </p>
                  <h3 className="mt-3 font-display text-xl md:text-2xl tracking-editorial leading-tight group-hover:text-ember transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-char-mute group-hover:text-bone/70 transition-colors leading-relaxed line-clamp-3">
                    {p.dek}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-wider2 font-bold text-ember">
                    Read article <ArrowUpRight size={12} strokeWidth={2} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <SisterServices keys={["menu", "ubereats", "cafe", "gbp"]} />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Reading time over."
        italic="Time to shoot."
        intro="Most quotes confirmed in under five minutes."
        backdropSlug="hero-spread-01"
      />
      <Footer />
    </div>
  );
}
